import { Expand, Minimize } from 'lucide-react'
import { memo } from 'react'
import { CopyableContent } from '@/components/common/CopyableContent'
import { NodeTextarea } from '@/components/common/NodeTextarea'
import { StatusIndicator } from '@/components/common/StatusIndicator'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'

interface CompositeContentProps {
  /** Markdown content value */
  content: string
  /** Handler for content changes */
  onContentChange: (value: string) => void
  /** Handler for content blur (sync to collaborative state) */
  onContentBlur: () => void
  /** Current status message */
  statusMessage: string
  /** Whether markdown preview is expanded */
  isMarkdownExpanded: boolean
  /** Handler for toggling markdown preview */
  onMarkdownToggle: () => void
  /** Whether the node is in read-only mode */
  readOnly?: boolean
  /** Whether to show the copy button */
  showCopyButton?: boolean
  /** Whether the content has unsaved changes */
  hasUnsyncedChanges?: boolean
}

/**
 * Content area component for the Composite Node.
 * Handles markdown editing, preview, and status display.
 */
export const CompositeContent = memo<CompositeContentProps>(({
  content,
  onContentChange,
  onContentBlur,
  statusMessage,
  isMarkdownExpanded,
  onMarkdownToggle,
  readOnly = false,
  showCopyButton = true,
  hasUnsyncedChanges = false
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Status indicator */}
      {statusMessage && (
        <div className="mb-2">          <StatusIndicator 
            status={hasUnsyncedChanges ? 'warning' : 'info'}
            message={statusMessage}
          />
        </div>
      )}

      {/* Content editor/preview area */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-t-md">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Content
            {hasUnsyncedChanges && (
              <span className="ml-2 text-xs text-orange-500">• Unsaved</span>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkdownToggle}
            className="h-8 w-8 p-0"
            title={isMarkdownExpanded ? 'Collapse preview' : 'Expand preview'}
          >
            {isMarkdownExpanded ? (
              <Minimize className="h-4 w-4" />
            ) : (
              <Expand className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Content area */}
        <div className="flex-1 border rounded-b-md">
          {isMarkdownExpanded ? (
            /* Preview mode */
            <div className="grid grid-cols-2 h-full">
              {/* Editor */}              <div className="border-r">
                <NodeTextarea
                  value={content}
                  onChange={onContentChange}
                  onBlur={onContentBlur}
                  placeholder="Enter your content in Markdown format..."
                  disabled={readOnly}
                  showCopy={showCopyButton}
                  className="h-full border-0 rounded-none"
                />
              </div>
              
              {/* Preview */}
              <div className="flex flex-col">
                <div className="p-2 bg-gray-50 dark:bg-gray-800 border-b">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Preview
                  </span>
                </div>
                <ScrollArea className="flex-1 p-4">                  <CopyableContent 
                    content={content}
                    position="top-right"
                  >
                    <div 
                      className="prose dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: content || '<p class="text-gray-500 italic">Preview will appear here...</p>' 
                      }}
                    />
                  </CopyableContent>
                </ScrollArea>
              </div>
            </div>
          ) : (
            /* Editor only mode */            <NodeTextarea
              value={content}
              onChange={onContentChange}
              onBlur={onContentBlur}
              placeholder="Enter your content in Markdown format..."
              disabled={readOnly}
              showCopy={showCopyButton}
              className="h-full border-0 rounded-none"
            />
          )}
        </div>
      </div>
    </div>
  )
})
